/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


const MIN_TIME = 0;

const MAX_TIME = 7 * 24 * 60 * 60 * 1000; // 7 days

const EQUILIBRIUM_TOLERANCE = 1e-2;
const CONVERGENCE_TOLERANCE = 1e-7;

const MAX_ITERATIONS = 1000;


const isExponentialDecay = (value, threshold) =>
{
  return value > threshold;
};


const getExponentialSteadyState = (userConfig) =>
{
  return userConfig.influence / userConfig.exponentialAttrition;
};


const getLinearRate = (userConfig, globalConfig) =>
{
  return userConfig.influence - userConfig.linearAttrition * globalConfig.threshold;
};


const getLinearValue = (value, rounds, userConfig, globalConfig) =>
{
  return value + getLinearRate(userConfig, globalConfig) * rounds;
};


const getExponentialValue = (value, rounds, userConfig, globalConfig) =>
{
  const steadyState = getExponentialSteadyState(userConfig);

  return steadyState + (value - steadyState) * Math.exp(- userConfig.exponentialAttrition * rounds);
};


const getLinearRounds = (value, target, userConfig, globalConfig) =>
{
  const rate = getLinearRate(userConfig, globalConfig);

  if (target === value)
  {
    return 0;
  }

  if (rate === 0)
  {
    return -Infinity;
  }

  return (target - value) / rate;
};


const getExponentialRounds = (value, target, userConfig, globalConfig) =>
{
  const steadyState = getExponentialSteadyState(userConfig);

  const valueDelta = value - steadyState;
  const targetDelta = target - steadyState;

  if (valueDelta === 0 || targetDelta / valueDelta <= 0)
  {
    return -Infinity;
  }

  return -1.0 / userConfig.exponentialAttrition * Math.log(targetDelta / valueDelta);
};


const getValueInternal = (value, elapsed, userConfig, globalConfig) =>
{
  const rounds = elapsed / globalConfig.roundDuration;

  if (isExponentialDecay(value, globalConfig.threshold))
  {
    const thresholdRounds = getExponentialRounds(value, globalConfig.threshold, userConfig, globalConfig);

    if (thresholdRounds < 0 || rounds <= thresholdRounds)
    {
      return getExponentialValue(value, rounds, userConfig, globalConfig);
    }

    if (getLinearRate(userConfig, globalConfig) >= 0)
    {
      return globalConfig.threshold;
    }

    return getLinearValue(globalConfig.threshold, rounds - thresholdRounds, userConfig, globalConfig);
  }

  const thresholdRounds = getLinearRounds(value, globalConfig.threshold, userConfig, globalConfig);

  if (getLinearRate(userConfig, globalConfig) <= 0 || rounds <= thresholdRounds)
  {
    return getLinearValue(value, rounds, userConfig, globalConfig);
  }

  if (getExponentialSteadyState(userConfig) <= globalConfig.threshold)
  {
    return globalConfig.threshold;
  }

  return getExponentialValue(globalConfig.threshold, rounds - thresholdRounds, userConfig, globalConfig);
};


export const getValue = (value, elapsed, userConfig, globalConfig) =>
{
  return Math.max(0, getValueInternal(value, elapsed, userConfig, globalConfig));
};


export const getEquilibriumValue = (value, userConfig, globalConfig) =>
{
  const linearRate = getLinearRate(userConfig, globalConfig);

  const exponentialSteadyState = getExponentialSteadyState(userConfig);

  if (isExponentialDecay(value, globalConfig.threshold))
  {
    if (exponentialSteadyState >= globalConfig.threshold)
    {
      return exponentialSteadyState;
    }

    return linearRate >= 0 ? globalConfig.threshold : 0;
  }

  if (linearRate > 0)
  {
    return exponentialSteadyState > globalConfig.threshold ? exponentialSteadyState : globalConfig.threshold;
  }

  return linearRate === 0 ? value : 0;
};


export const getRounds = (value, target, userConfig, globalConfig) =>
{
  return getTicks(value, target, userConfig, globalConfig) / globalConfig.roundDuration;
};


export const getRoundsToEqual = (valueLhs, valueRhs, userConfigLhs, userConfigRhs, globalConfig) =>
{
  return getTicksToEqual(valueLhs, valueRhs, userConfigLhs, userConfigRhs, globalConfig) / globalConfig.roundDuration;
};


export const getTicks = (value, target, userConfig, globalConfig) =>
{
  if (value > globalConfig.threshold && target > globalConfig.threshold)
  {
    return Math.ceil(getExponentialRounds(value, target, userConfig, globalConfig) * globalConfig.roundDuration);
  }

  if (value > globalConfig.threshold && target <= globalConfig.threshold)
  {
    const thresholdRounds = getExponentialRounds(value, globalConfig.threshold, userConfig, globalConfig);

    if (thresholdRounds < 0)
    {
      return -Infinity;
    }

    if (target < globalConfig.threshold && getLinearRate(userConfig, globalConfig) >= 0)
    {
      return -Infinity;
    }

    return Math.ceil((thresholdRounds + getLinearRounds(globalConfig.threshold, target, userConfig, globalConfig)) * globalConfig.roundDuration);
  }

  if (value <= globalConfig.threshold && target > globalConfig.threshold)
  {
    const thresholdRounds = getLinearRounds(value, globalConfig.threshold, userConfig, globalConfig);

    if (getLinearRate(userConfig, globalConfig) <= 0 || getExponentialSteadyState(userConfig) <= globalConfig.threshold)
    {
      return -Infinity;
    }

    return Math.ceil((thresholdRounds + getExponentialRounds(globalConfig.threshold, target, userConfig, globalConfig)) * globalConfig.roundDuration);
  }

  return Math.ceil(getLinearRounds(value, target, userConfig, globalConfig) * globalConfig.roundDuration);
};


const getTickSegments = (valueLhs, valueRhs, userConfigLhs, userConfigRhs, globalConfig) =>
{
  const ticksWithinBounds = (ticks) =>
  {
    return ticks >= MIN_TIME && ticks <= MAX_TIME;
  };

  const segments = new Set([ MIN_TIME, MAX_TIME ]);

  segments.add(getTicks(valueLhs, globalConfig.threshold, userConfigLhs, globalConfig));
  segments.add(getTicks(valueRhs, globalConfig.threshold, userConfigRhs, globalConfig));

  return [ ...segments ].filter(ticksWithinBounds).sort((lhs, rhs) => lhs - rhs);
};


export const getTicksToEqual = (valueLhs, valueRhs, userConfigLhs, userConfigRhs, globalConfig) =>
{
  if (valueLhs === valueRhs)
  {
    return 0;
  }

  const getDifference = (ticks) =>
  {
    return getValueInternal(valueLhs, ticks, userConfigLhs, globalConfig) - getValueInternal(valueRhs, ticks, userConfigRhs, globalConfig);
  };

  const tickSegments = getTickSegments(valueLhs, valueRhs, userConfigLhs, userConfigRhs, globalConfig);

  for (let i = 1; i < tickSegments.length; ++i)
  {
    let minTicks = tickSegments[i - 1];
    let maxTicks = tickSegments[i];

    if (getDifference(minTicks) * getDifference(maxTicks) > 0)
    {
      continue;
    }

    let iteration = 0;

    while (iteration < MAX_ITERATIONS)
    {
      const midTicks = (minTicks + maxTicks) / 2;

      const midDifference = getDifference(midTicks);

      if (Math.abs(midDifference) < CONVERGENCE_TOLERANCE)
      {
        if (getValue(valueLhs, midTicks, userConfigLhs, globalConfig) === 0)
        {
          return Math.max(getTicks(valueLhs, 0, userConfigLhs, globalConfig), getTicks(valueRhs, 0, userConfigRhs, globalConfig));
        }

        return Math.ceil(midTicks);
      }

      if (getDifference(minTicks) * midDifference < 0)
      {
        maxTicks = midTicks;
      }
      else
      {
        minTicks = midTicks;
      }

      ++iteration;
    }

    return -Infinity;
  }

  return -Infinity;
};


export const getTicksToEquilibrium = (value, userConfig, globalConfig) =>
{
  const equilibrium = getEquilibriumValue(value, userConfig, globalConfig);

  if (Math.abs(value - equilibrium) <= EQUILIBRIUM_TOLERANCE)
  {
    return 0;
  }

  const target = equilibrium + (value > equilibrium ? EQUILIBRIUM_TOLERANCE : - EQUILIBRIUM_TOLERANCE);

  return getTicks(value, target, userConfig, globalConfig);
};


export const getControlRate = (value, userConfig, globalConfig) =>
{
  return getValue(value, globalConfig.roundDuration, userConfig, globalConfig) - value;
};
